import { range } from 'lodash-es'
import { DateTime, type Duration } from 'luxon'

import { pluralize } from '../helpers'

export const DEFAULT_WEEK_START_DAY = 1

const SECONDS_PER_MINUTE = 60
export const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR
export const MINUTES_PER_HOUR = 60
export const SECONDS_PER_WEEK = 7 * SECONDS_PER_DAY

export function millisecondsToHumanReadableString(
  millis: number,
  params: { maxUnit?: 'second' | 'minute' | 'hour' | 'day' | 'week'; omitSecondary?: boolean } = {}
): string {
  const { maxUnit, omitSecondary = false } = params

  const seconds = Math.floor(millis / 1000)
  if (seconds < SECONDS_PER_MINUTE || maxUnit === 'second') {
    return pluralize(seconds, 'second')
  }
  if (seconds < 5 * SECONDS_PER_MINUTE) {
    const minutes = Math.floor(seconds / SECONDS_PER_MINUTE)
    const remainingSeconds = seconds - minutes * SECONDS_PER_MINUTE
    return `${pluralize(minutes, 'minute')}${
      remainingSeconds > 0 && !omitSecondary ? ` and ${pluralize(remainingSeconds, 'second')}` : ''
    }`
  }
  if (seconds < SECONDS_PER_HOUR || maxUnit === 'minute') {
    const minutes = Math.ceil(seconds / SECONDS_PER_MINUTE)
    return pluralize(minutes, 'minute')
  }
  if (seconds < SECONDS_PER_DAY || maxUnit === 'hour') {
    const hours = Math.floor(seconds / SECONDS_PER_HOUR)
    const minutes = Math.floor((seconds - hours * SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)
    return `${pluralize(hours, 'hour')}${
      minutes > 0 && !omitSecondary ? ` and ${pluralize(minutes, 'minute')}` : ''
    }`
  }
  if (seconds < SECONDS_PER_DAY * 7 || maxUnit === 'day') {
    const days = Math.floor(seconds / SECONDS_PER_DAY)
    const hours = Math.floor((seconds - days * SECONDS_PER_DAY) / SECONDS_PER_HOUR)
    return `${pluralize(days, 'day')}${
      hours > 0 && !omitSecondary ? ` and ${pluralize(hours, 'hour')}` : ''
    }`
  }
  const days = Math.floor(seconds / SECONDS_PER_DAY)
  if (seconds < SECONDS_PER_DAY * 30) {
    return pluralize(days, 'day')
  }
  const weeks = Math.floor(seconds / SECONDS_PER_WEEK)
  const daysLeft = Math.floor((seconds - weeks * SECONDS_PER_WEEK) / SECONDS_PER_DAY)
  return `${pluralize(weeks, 'week')}${
    daysLeft > 0 && !omitSecondary ? ` and ${pluralize(daysLeft, 'day')}` : ''
  }`
}

export function toHumanReadableString(
  duration: Duration,
  params: { maxUnit?: 'second' | 'minute' | 'hour' | 'day'; omitSecondary?: boolean } = {}
): string {
  if (!duration.isValid) {
    return 'Invalid Date'
  }
  const millis = duration.toMillis()
  if (Number.isNaN(millis)) {
    return 'Invalid Date'
  }
  return millis < 0
    ? `${millisecondsToHumanReadableString(-millis, params)} ago`
    : `in ${millisecondsToHumanReadableString(millis, params)}`
}

export function getEvenlyDistributedDateTimes({
  start,
  end,
  count,
}: {
  start: DateTime
  end: DateTime
  count: number
}): DateTime[] {
  const duration = end.diff(start)
  const interval = duration.as('milliseconds') / count

  return range(0, count).map(i => start.plus(interval * i))
}

export function toDateAndTimeString(date: Date) {
  return DateTime.fromJSDate(date).toFormat('MMM d, yyyy at h:mm a')
}

export { default as getClosestDateTime } from './getClosestDateTime'
export { default as getNextWeeks } from './getNextWeeks'
export { default as getRelativeTimeShort } from './getRelativeTimeShort'
